/**
 * CombatSystem.ts
 * Target acquisition, attack timing, damage, and death for all units.
 *
 * Design: purely data-driven — reads / writes Unit fields, no graphics.
 * UnitRenderer reads unit.hitFlash to apply a red tint when a unit is struck.
 *
 * Each frame:
 *   1. Decay hit-flash timers on all living units.
 *   2. Player units scan for enemies within sightRadius; chase or attack.
 *   3. Enemy units scan for player units; attack if found, otherwise pressure
 *      the most useful objective instead of blindly piling onto the Beacon.
 */

import type { Unit }              from '../entities/Unit';
import { Faction }                from '../types';
import type { GridPosition }      from '../types';
import type { RhythmSystem }      from './RhythmSystem';
import type { PathfindingSystem } from './PathfindingSystem';
import type { ResourceSystem }    from './ResourceSystem';
import type { VenueNode }         from './BuildSystem';
import type { SpatialIndex }      from './SpatialIndex';
import type { TileMap }           from '../map/TileMap';
import {
  ATTACK_PULSE_DURATION,
  BAND_REVIVE_HP_RATIO,
  BAND_REVIVE_RANGE,
  BAND_REVIVE_TIME,
  SKILL_ATTACK_BOOST_MULT,
  BADMANAGER_CORRUPT_DUR,
  BADMANAGER_DETECTION_RADIUS,
  BADMANAGER_DETECTED_STUN,
  MOGUL_AURA_INTERVAL,
  MOGUL_AURA_JAM_DUR,
  MOGUL_AURA_RADIUS,
  GENZ_BURST_INTERVAL,
  GENZ_BURST_DUR,
  TRIBUTE_RALLY_RADIUS,
  TRIBUTE_RALLY_MIN_ALLIES,
} from '../constants';

/** Predicate type used to select opponent units in spatial queries. */
type UnitFilter = (u: Unit) => boolean;
type TorrentShotCallback = (from: GridPosition, to: GridPosition) => void;
type UnitCallback = (unit: Unit) => void;

export class CombatSystem {
  private venues: VenueNode[] = [];
  private activeUnits: Unit[] = [];

  // ── Spatial index (injected each tick via update()) ───────────────────
  private spatial!: SpatialIndex;

  // ── Zero-allocation unit caches (reused every tick) ───────────────────
  private readonly aliveArr:  Unit[] = [];
  private readonly playerArr: Unit[] = [];
  private readonly enemyArr:  Unit[] = [];

  /**
   * Pre-allocated output buffer for SpatialIndex.nearby() — zero-allocation
   * on the hot path.  Never expose or retain references to this array.
   */
  private readonly queryBuf: Unit[] = [];
  private readonly detectedManagers = new Set<string>();

  // ── Reusable opponent-filter functions (allocated once at class init) ──

  /** Neutral Music Press is a side objective, not a generic auto-combat target. */
  private readonly isPlayerOpponent: UnitFilter =
    (u) => u.faction === Faction.Enemy && u.enemyType !== null;

  /** True for any unit the enemy side can target (player-faction units). */
  private readonly isEnemyOpponent: UnitFilter =
    (u) => u.faction === Faction.Player;

  constructor(
    private readonly map:         TileMap,
    private readonly rhythm:      RhythmSystem,
    private readonly pathfinding: PathfindingSystem,
    private readonly resources:   ResourceSystem,
    private readonly onTorrentShot?: TorrentShotCallback,
    private readonly onMusicPressConverted?: UnitCallback,
    private readonly onBadManagerDetected?: UnitCallback,
  ) {}

  /** Called by Game.ts once BuildSystem is ready. */
  setVenues(venues: VenueNode[]): void {
    this.venues = venues;
  }

  /** Returns the nearest threatened objective for this enemy unit. */
  private pickObjective(enemy: Unit): GridPosition {
    if (enemy.specialRole === 'rivalVenueBuilder' && enemy.objectiveTarget) {
      return { ...enemy.objectiveTarget };
    }

    if (enemy.specialRole === 'streamingMonolith' && enemy.objectiveTarget) {
      return { ...enemy.objectiveTarget };
    }

    if (enemy.displayName === 'Monolith Guard') {
      const monolith = this.activeUnits.find(unit =>
        unit.alive && unit.specialRole === 'streamingMonolith',
      );
      if (monolith) return { ...monolith.gridPos };
    }

    const rivalBeacon = this.activeBaddieBeacon();
    if (rivalBeacon && this.shouldDefendRivalBeacon(enemy)) {
      return { ...rivalBeacon.gridPos };
    }

    if (enemy.displayName === 'Signal Jammer') {
      return { ...this.map.beacon };
    }

    const playerPressure = this.pickPlayerPressureObjective(enemy);
    if (playerPressure && (this.shouldHuntPlayers(enemy) || this.enemyClusterCount(this.map.beacon, 5) >= 7)) {
      return playerPressure;
    }

    let best: GridPosition = this.map.beacon;
    let bestDist = Math.hypot(
      enemy.gridPos.col - this.map.beacon.col,
      enemy.gridPos.row - this.map.beacon.row,
    );
    for (const v of this.venues) {
      if (v.state !== 'claimed') continue;
      const d = Math.hypot(enemy.gridPos.col - v.col, enemy.gridPos.row - v.row);
      if (d < bestDist) { best = { col: v.col, row: v.row }; bestDist = d; }
    }
    return best;
  }

  private shouldDefendRivalBeacon(enemy: Unit): boolean {
    return enemy.specialRole === 'streamingMonolith' ||
      enemy.displayName === 'Repair Crew' ||
      enemy.displayName === 'Monolith Guard';
  }

  private shouldHuntPlayers(enemy: Unit): boolean {
    if (enemy.specialRole === 'rivalVenueBuilder' || enemy.specialRole === 'rivalVenue') return false;
    if (enemy.specialRole === 'baddieBeacon' || enemy.specialRole === 'corporateHQ') return false;
    return enemy.enemyType === 'GenZSwarm' ||
      enemy.enemyType === 'BadManager' ||
      enemy.enemyType === 'TributeAct' ||
      enemy.enemyType === 'FilePirate' ||
      enemy.displayName === 'Baddie Worker';
  }

  private pickPlayerPressureObjective(enemy: Unit): GridPosition | null {
    let best: Unit | null = null;
    let bestScore = Infinity;
    for (const player of this.playerArr) {
      if (!this.canBeTargeted(player)) continue;
      if (player.bandClass === 'source') continue;
      // Never specifically hunt the unit that is actively mid-claim on a venue —
      // rushing claimers was causing all venue captures to be interrupted.
      if (this.venues.some(v => v.state === 'claiming' && v.claimerUnit === player)) continue;

      const distance = this.dist(enemy, player);
      const beaconDistance = Math.hypot(
        player.gridPos.col - this.map.beacon.col,
        player.gridPos.row - this.map.beacon.row,
      );
      let score = distance;

      if (player.bandClass === 'fan') score -= 8;
      if (player.isMining) score -= 10;
      if (beaconDistance < 4) score += 7;

      if (enemy.enemyType === 'BadManager' && player.bandClass === 'promoter') score -= 18;
      if (enemy.enemyType === 'TributeAct' && player.bandClass === 'protector') score -= 16;
      if (enemy.enemyType === 'FilePirate' && player.bandClass === 'fan') score -= 4;

      if (score < bestScore) {
        best = player;
        bestScore = score;
      }
    }
    return best ? { ...best.gridPos } : null;
  }

  private enemyClusterCount(center: GridPosition, radius: number): number {
    let count = 0;
    for (const unit of this.enemyArr) {
      if (!unit.alive || unit.isDowned) continue;
      const d = Math.hypot(unit.gridPos.col - center.col, unit.gridPos.row - center.row);
      if (d <= radius) count++;
    }
    return count;
  }

  /**
   * Run one combat frame.
   *
   * `spatial` must be a SpatialIndex already rebuilt from the current unit
   * positions this tick (Game.ts rebuilds it once, just before calling here).
   *
   * Unit arrays are reused across ticks (zero allocation) — one pass over
   * `units` replaces the previous four `.filter()` chains.
   */
  update(units: Unit[], delta: number, spatial: SpatialIndex): void {
    this.spatial = spatial;

    // Rebuild cached faction arrays — single O(n) pass, no allocation.
    this.aliveArr.length  = 0;
    this.playerArr.length = 0;
    this.enemyArr.length  = 0;
    for (const u of units) {
      if (!u.alive) continue;
      this.aliveArr.push(u);
      if      (u.faction === Faction.Player) this.playerArr.push(u);
      else if (u.faction === Faction.Enemy)  this.enemyArr.push(u);
    }
    this.activeUnits = this.aliveArr;

    // Decay hit-flash tint and spell effect timers on every living unit
    for (const u of this.aliveArr) {
      if (u.hitFlash      > 0) u.hitFlash      = Math.max(0, u.hitFlash      - delta);
      if (u.attackPulse   > 0) u.attackPulse   = Math.max(0, u.attackPulse   - delta);
      if (u.confusedTimer > 0) u.confusedTimer = Math.max(0, u.confusedTimer - delta);
      if (u.moveSlowTimer > 0) u.moveSlowTimer = Math.max(0, u.moveSlowTimer - delta);
    }

    this.tickRevives(this.playerArr, delta);

    // Player units can fight any opposition piece, including neutral Music
    // Press before it turns hostile. Only hostile enemies attack back.
    this.tick(this.playerArr, this.isPlayerOpponent, delta, false);
    this.tick(this.enemyArr,  this.isEnemyOpponent,  delta, true);
  }

  // ── Per-faction tick ──────────────────────────────────────────────────

  private tick(
    actors:       Unit[],
    isOpponent:   UnitFilter,
    delta:        number,
    walkToBeacon: boolean,
  ): void {
    for (const actor of actors) {
      if (!this.canAct(actor)) continue;

      // Crowd Confusion: confused enemies target their own faction instead
      const effectiveOpponent: UnitFilter = (actor.confusedTimer > 0)
        ? (u) => u.faction === actor.faction && u !== actor && u.alive && !u.isDowned
        : isOpponent;

      // Advance attack cooldown — Tempo Surge (Engine skill) speeds this up for player units
      if (actor.attackCooldown > 0) {
        const boostMult = actor.attackBoostTimer > 0 ? SKILL_ATTACK_BOOST_MULT : 1;
        actor.attackCooldown = Math.max(0, actor.attackCooldown - delta * boostMult);
      }

      // ── Unit-type special AI behaviors ───────────────────────────────────

      // StreamingMogul: interference aura pulses slow nearby player units
      if (actor.enemyType === 'StreamingMogul' && walkToBeacon) {
        actor.auraTimer -= delta;
        if (actor.auraTimer <= 0) {
          actor.auraTimer = MOGUL_AURA_INTERVAL;
          this.spatial.nearby(actor.gridPos.col, actor.gridPos.row, MOGUL_AURA_RADIUS, this.queryBuf);
          for (const u of this.queryBuf) {
            if (u.faction === Faction.Player && u.alive && !u.isDowned) {
              u.moveSlowTimer = Math.max(u.moveSlowTimer, MOGUL_AURA_JAM_DUR);
            }
          }
        }
        if (this.isPassiveMogul(actor)) {
          // Ordinary Moguls pressure the economy and Beacon passively. Special
          // Monolith variants retain their explicit defender/boss combat role.
          actor.target = null;
          actor.orderTargetUnit = null;
          if (actor.combatOrder === 'attackTarget') actor.combatOrder = 'idle';
        }
      }

      // GenZSwarm: periodic burst sprint — timer cycling
      if (actor.enemyType === 'GenZSwarm' && walkToBeacon) {
        if (actor.burstTimer > 0) {
          actor.burstTimer = Math.max(0, actor.burstTimer - delta);
        }
        actor.burstCycleTimer -= delta;
        if (actor.burstCycleTimer <= 0) {
          actor.burstCycleTimer = GENZ_BURST_INTERVAL;
          actor.burstTimer = GENZ_BURST_DUR;
        }
      }

      if (walkToBeacon && actor.enemyType === 'FilePirate' && this.tickFilePirate(actor, effectiveOpponent)) {
        continue;
      }

      // Drop dead targets, then decide whether the current order should keep
      // flowing into the next nearby fight or settle back to idle/move.
      if (actor.target && !this.canBeTargeted(actor.target)) {
        actor.target = null;
        if (actor.orderTargetUnit && !this.canBeTargeted(actor.orderTargetUnit)) {
          actor.orderTargetUnit = null;
          if (actor.combatOrder === 'attackTarget') actor.combatOrder = 'attackMove';
        }
      }

      // Re-acquire nearest target if we don't have one
      if (!actor.target) {
        actor.target = this.acquireTarget(actor, effectiveOpponent, walkToBeacon);
      }

      if (actor.target) {
        const d = this.dist(actor, actor.target);

        if (d > 0 && d <= actor.attackRadius) {
          // In melee range — halt and swing
          actor.path  = [];
          actor.moveT = 0;
          if (actor.attackCooldown === 0) {
            this.strike(actor, actor.target);
            actor.attackCooldown = 1 / actor.attackRate;
          }

        } else if (d <= this.chaseRadius(actor, walkToBeacon)) {
          // Target visible but out of range — chase.
          //
          // PLAYER UNITS: only re-path when idle so manual move orders are
          // respected — a unit already walking keeps its current path.
          //
          // ENEMY UNITS (walkToBeacon=true): also re-path when the path head
          // is stale (target has moved more than 1.5 tiles from path[0]).
          const dest = this.approachTile(actor, actor.target);
          const blocked = this.pathHeadBlocked(actor, actor.target);
          if (walkToBeacon) {
            const head  = actor.path[0];
            const stale = !head ||
              Math.hypot(head.col - dest.col, head.row - dest.row) > 1.5;
            if (!actor.isMoving || stale || blocked) {
              actor.setPath(this.pathfinding.findPath(actor.gridPos, dest));
            }
          } else if (!actor.isMoving || blocked) {
            actor.setPath(this.pathfinding.findPath(actor.gridPos, dest));
          }

        } else {
          // Target left sight radius — disengage
          actor.target = null;
        }

      } else if (actor.combatOrder === 'attackMove' && actor.orderTarget && !actor.isMoving) {
        actor.combatOrder = 'idle';
        actor.orderTarget = null;
      } else if (walkToBeacon && !actor.isMoving && actor.confusedTimer <= 0) {
        // Enemy with no target — march toward objective.
        // Retreating units flee to the nearest map edge.
        if (actor.isRetreating) {
          const dest = this.retreatPosition(actor);
          actor.setPath(this.pathfinding.findPath(actor.gridPos, dest));
          continue;
        }
        // TributeAct holds until it has enough nearby allies (rally formation).
        if (actor.enemyType === 'TributeAct' && !actor.rallyReady) {
          this.spatial.nearby(actor.gridPos.col, actor.gridPos.row, TRIBUTE_RALLY_RADIUS, this.queryBuf);
          const allies = this.queryBuf.filter(u =>
            u !== actor && u.faction === Faction.Enemy && u.alive && !u.isDowned,
          );
          if (allies.length >= TRIBUTE_RALLY_MIN_ALLIES) {
            actor.rallyReady = true;
          } else {
            continue; // hold — wait for backup
          }
        }
        // BattlePlanner commandTarget overrides default objective, except for
        // builders that must stay committed to their construction site.
        const objective = actor.specialRole === 'rivalVenueBuilder' || this.shouldOverrideBeaconCommand(actor)
          ? this.pickObjective(actor)
          : actor.commandTarget ?? this.pickObjective(actor);
        const dest      = this.marchTile(actor, objective);
        actor.setPath(this.pathfinding.findPath(actor.gridPos, dest));
      }
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  private acquireTarget(actor: Unit, isOpponent: UnitFilter, walkToBeacon: boolean): Unit | null {
    if (!this.canAct(actor)) return null;

    if (walkToBeacon && this.isPassiveMogul(actor)) return null;

    if (actor.specialRole === 'streamingMonolith' && actor.objectiveTarget && !actor.isConstructing) {
      return null;
    }

    if (actor.specialRole === 'rivalVenueBuilder' || actor.specialRole === 'rivalVenue') {
      return null;
    }

    if (actor.combatOrder === 'move' && !walkToBeacon) return null;

    if (actor.combatOrder === 'attackTarget' && actor.orderTargetUnit) {
      if (this.canBeTargeted(actor.orderTargetUnit)) return actor.orderTargetUnit;
      actor.orderTargetUnit = null;
    }

    const vulnerableFan  = this.nearestVulnerableFan(actor, isOpponent);
    const rivalBeacon    = this.activeBaddieBeacon();
    const rivalR         = actor.sightRadius * 1.5;
    const nearRivalBeacon = !!rivalBeacon && this.dist(actor, rivalBeacon) <= rivalR;

    if (
      nearRivalBeacon &&
      (
        actor.displayName === 'Baddie Worker' ||
        actor.displayName === 'Repair Crew'   ||
        actor.displayName === 'Signal Jammer' ||
        actor.specialRole === 'streamingMonolith'
      )
    ) {
      // Prefer opponents clustered around the rival beacon; fall back to wider search.
      const rb = rivalBeacon!;
      return this.findNearest(actor, rivalR, u => isOpponent(u) && this.dist(u, rb) <= rivalR)
          ?? vulnerableFan
          ?? this.findNearest(actor, actor.sightRadius, isOpponent);
    }

    if (actor.enemyType === 'BadManager') {
      return this.findNearest(actor, rivalR, u => isOpponent(u) && u.bandClass === 'promoter')
          ?? vulnerableFan
          ?? this.findNearest(actor, actor.sightRadius, isOpponent);
    }

    if (actor.enemyType === 'TributeAct') {
      return this.findNearest(actor, actor.sightRadius, u => isOpponent(u) && u.bandClass === 'protector')
          ?? this.findNearest(actor, actor.sightRadius, isOpponent);
    }

    return vulnerableFan ?? this.findNearest(actor, actor.sightRadius, isOpponent);
  }

  private strike(attacker: Unit, target: Unit): void {
    if (!this.canAct(attacker) || !this.canBeTargeted(target)) return;

    if (target.enemyType === 'MusicPress' && target.faction === Faction.Neutral) {
      if (attacker.faction === Faction.Player && attacker.bandClass === 'promoter') {
        target.faction = Faction.Player;
        target.pressHostileTimer = null;
        target.displayName = 'Allied Music Press';
        target.clearCombatOrder();
        attacker.path = [];
        attacker.moveT = 0;
        attacker.clearCombatOrder();
        this.onMusicPressConverted?.(target);
      } else {
        attacker.path = [];
        attacker.moveT = 0;
        attacker.clearCombatOrder();
      }
      return;
    }

    if (attacker.attackDamage <= 0) return;

    // BadManager corrupts player units instead of dealing HP damage
    if (attacker.enemyType === 'BadManager' && target.faction === Faction.Player) {
      if (this.keyboardProphetsDetect(attacker)) {
        attacker.target = null;
        attacker.path = [];
        attacker.moveT = 0;
        attacker.stunTimer = Math.max(attacker.stunTimer, BADMANAGER_DETECTED_STUN);
        attacker.hitFlash = 0.5;
        if (!this.detectedManagers.has(attacker.id)) {
          this.detectedManagers.add(attacker.id);
          this.onBadManagerDetected?.(attacker);
        }
        return;
      }
      attacker.attackPulse  = ATTACK_PULSE_DURATION;
      target.confusedTimer  = Math.max(target.confusedTimer, BADMANAGER_CORRUPT_DUR);
      target.hitFlash       = 0.35;
      return;
    }

    attacker.attackPulse = ATTACK_PULSE_DURATION;
    if (attacker.enemyType === 'FilePirate') {
      this.onTorrentShot?.(attacker.gridPos, target.gridPos);
    }
    const damage = this.damageAfterTacticalModifiers(attacker, target);
    target.hp       = Math.max(0, target.hp - damage);
    target.hitFlash = 0.18; // seconds of red tint

    if (target.hp <= 0) {
      if (this.isReviveableBandMember(target)) {
        this.downBandMember(target);
      } else {
        target.alive = false;
        if (attacker.faction === Faction.Enemy && target.bandClass === 'fan') {
          this.resources.onFanKilledByEnemy();
        }
      }
      // Chris (engine class) going down smashes tempo to zero
      if (target.bandClass === 'engine') this.rhythm.onChrisKilled();
    } else if (target.bandClass === 'engine') {
      // Any hit on Chris nudges tempo down
      this.rhythm.onChrisHit();
    }
  }

  private keyboardProphetsDetect(manager: Unit): boolean {
    return this.playerArr.some(unit =>
      unit.alive &&
      !unit.isDowned &&
      unit.bandClass === 'mystic' &&
      this.dist(unit, manager) <= BADMANAGER_DETECTION_RADIUS,
    );
  }

  private isPassiveMogul(unit: Unit): boolean {
    return unit.enemyType === 'StreamingMogul' &&
      unit.specialRole !== 'streamingMonolith' &&
      unit.displayName !== 'Monolith Guard';
  }

  private tickFilePirate(actor: Unit, isOpponent: UnitFilter): boolean {
    const threatened = this.findNearest(actor, actor.sightRadius, isOpponent);
    if (threatened && this.dist(actor, threatened) <= 2.5) {
      const escape = this.retreatTileFrom(actor, threatened.gridPos);
      if (escape) {
        actor.target = null;
        actor.setPath(this.pathfinding.findPath(actor.gridPos, escape));
        return true;
      }
    }

    const venue = this.nearestClaimedVenueInRange(actor, actor.attackRadius);
    if (venue) {
      actor.target = null;
      actor.path = [];
      actor.moveT = 0;
      if (actor.attackCooldown === 0) {
        this.strikeVenue(actor, venue);
        actor.attackCooldown = 1 / actor.attackRate;
      }
      return true;
    }

    const target = this.nearestVulnerableFan(actor, isOpponent)
      ?? this.findNearest(actor, actor.attackRadius, isOpponent);
    if (target && this.dist(actor, target) <= actor.attackRadius) {
      actor.path = [];
      actor.moveT = 0;
      actor.target = target;
      if (actor.attackCooldown === 0) {
        this.strike(actor, target);
        actor.attackCooldown = 1 / actor.attackRate;
      }
      return true;
    }

    return false;
  }

  private strikeVenue(attacker: Unit, venue: VenueNode): void {
    if (!this.canAct(attacker) || venue.state !== 'claimed') return;
    attacker.attackPulse = ATTACK_PULSE_DURATION;
    if (attacker.enemyType === 'FilePirate') {
      this.onTorrentShot?.(attacker.gridPos, { col: venue.col, row: venue.row });
    }
    venue.isUnderAttack = true;
    venue.hp = Math.max(0, venue.hp - attacker.attackDamage);

    if (venue.hp > 0) return;

    this.resources.addVenueBonus(
      -venue.repBonus,
      -venue.salesBonus,
      -venue.fanBonus,
      -venue.gearBonus,
    );
    this.resources.onVenueLostToEnemy();
    venue.state = 'unclaimed';
    venue.progress = 0;
    venue.hp = venue.maxHp;
    venue.claimerUnit = null;
    venue.isUnderAttack = false;
  }

  private damageAfterTacticalModifiers(attacker: Unit, target: Unit): number {
    let damage = attacker.attackDamage;

    // Fans can finish fights, but they should not be able to delete boss
    // objectives by sheer crowd mass before the battle scenario develops.
    if (attacker.bandClass === 'fan') {
      if (target.specialRole === 'streamingMonolith') damage *= 0.25;
      if (target.specialRole === 'baddieBeacon') damage *= 0.45;
      if (target.specialRole === 'corporateHQ') damage *= 0.35;
      if (target.displayName === 'Corporate Tribute Act') damage *= 0.5;
    }

    // Early A&R units are meant to punish isolated fans enough to reveal friction,
    // not threaten the band by themselves.
    if (attacker.displayName === 'A&R' && target.bandClass === 'fan') {
      damage *= 1.6;
    }

    return Math.max(1, Math.round(damage));
  }

  /**
   * Find the nearest unit within `radius` tiles that passes `filter`.
   *
   * Uses the spatial index to pre-screen candidates — typically scans a handful
   * of nearby units rather than the full unit list, cutting the per-unit cost
   * from O(n) to O(k) where k ≈ bucket occupancy (usually ≤ 5).
   *
   * Writes into `this.queryBuf` (reused across calls — no allocation).
   */
  private findNearest(from: Unit, radius: number, filter: UnitFilter): Unit | null {
    this.spatial.nearby(from.gridPos.col, from.gridPos.row, radius, this.queryBuf);
    let best: Unit | null = null;
    let bestD = Infinity;
    for (const c of this.queryBuf) {
      if (!filter(c) || !this.canBeTargeted(c)) continue;
      const d = this.dist(from, c);
      if (d <= radius && d < bestD) { best = c; bestD = d; }
    }
    return best;
  }

  private dist(a: Unit, b: Unit): number {
    return Math.hypot(
      a.gridPos.col - b.gridPos.col,
      a.gridPos.row - b.gridPos.row,
    );
  }

  /**
   * Pick a formation tile for an enemy marching toward an objective with no
   * active target. Spreads enemies across tiles around the objective rather
   * than all converging on the same cell.
   */
  private marchTile(actor: Unit, objective: GridPosition): GridPosition {
    const candidates = this.pathfinding
      .formationTargets(objective, 16)
      .filter(pos => !this.isOccupied(pos, actor))
      .sort((a, b) => {
        const da = Math.hypot(actor.gridPos.col - a.col, actor.gridPos.row - a.row);
        const db = Math.hypot(actor.gridPos.col - b.col, actor.gridPos.row - b.row);
        return da - db;
      });
    return candidates[0] ?? objective;
  }

  private approachTile(actor: Unit, target: Unit): GridPosition {
    if (this.dist(actor, target) > 0 && this.dist(actor, target) <= actor.attackRadius) {
      return { ...actor.gridPos };
    }

    const candidates = this.pathfinding
      .formationTargets(target.gridPos, 12)
      .filter((pos) => {
        if (pos.col === target.gridPos.col && pos.row === target.gridPos.row) return false;
        if (Math.hypot(pos.col - target.gridPos.col, pos.row - target.gridPos.row) > actor.attackRadius) return false;
        return !this.isOccupied(pos, actor, target);
      })
      .sort((a, b) => {
        const da = Math.hypot(actor.gridPos.col - a.col, actor.gridPos.row - a.row);
        const db = Math.hypot(actor.gridPos.col - b.col, actor.gridPos.row - b.row);
        return da - db;
      });

    return candidates[0] ?? target.gridPos;
  }

  private pathHeadBlocked(actor: Unit, target?: Unit): boolean {
    const head = actor.path[0];
    return !!head && this.isOccupied(head, actor, target);
  }

  private isOccupied(pos: GridPosition, actor: Unit, target?: Unit): boolean {
    // Uses the spatial index for an O(bucket_size) exact-tile lookup instead
    // of the previous O(n) activeUnits.some() scan.
    for (const unit of this.spatial.atTile(pos.col, pos.row)) {
      if (unit === actor || unit === target || unit.isDowned) continue;
      // atTile() may return units in the same bucket — confirm exact tile.
      if (unit.gridPos.col === pos.col && unit.gridPos.row === pos.row) return true;
    }
    return false;
  }

  private nearestVulnerableFan(actor: Unit, isOpponent: UnitFilter): Unit | null {
    const fanRadius = actor.sightRadius * (actor.enemyType === 'GenZSwarm' ? 1.35 : 1.15);
    // Mining fans are highest priority; fall back to any fan in range.
    return this.findNearest(actor, fanRadius, u => isOpponent(u) && u.bandClass === 'fan' && u.isMining)
        ?? this.findNearest(actor, fanRadius, u => isOpponent(u) && u.bandClass === 'fan');
  }

  private shouldOverrideBeaconCommand(actor: Unit): boolean {
    if (!actor.commandTarget || !this.shouldHuntPlayers(actor)) return false;
    const toBeacon = Math.hypot(
      actor.commandTarget.col - this.map.beacon.col,
      actor.commandTarget.row - this.map.beacon.row,
    );
    return toBeacon <= 4 && this.enemyClusterCount(this.map.beacon, 5) >= 7;
  }

  private nearestClaimedVenueInRange(actor: Unit, radius: number): VenueNode | null {
    let best: VenueNode | null = null;
    let bestD = Infinity;
    for (const venue of this.venues) {
      if (venue.state !== 'claimed') continue;
      const d = Math.hypot(actor.gridPos.col - venue.col, actor.gridPos.row - venue.row);
      if (d <= radius && d < bestD) {
        best = venue;
        bestD = d;
      }
    }
    return best;
  }

  private retreatTileFrom(actor: Unit, threat: GridPosition): GridPosition | null {
    let best: GridPosition | null = null;
    let bestScore = -Infinity;
    for (const pos of this.pathfinding.formationTargets(actor.gridPos, 8)) {
      if (this.isOccupied(pos, actor)) continue;
      const fromThreat = Math.hypot(pos.col - threat.col, pos.row - threat.row);
      const fromActor = Math.hypot(pos.col - actor.gridPos.col, pos.row - actor.gridPos.row);
      const score = fromThreat - fromActor * 0.35;
      if (score > bestScore) {
        best = pos;
        bestScore = score;
      }
    }
    return best;
  }

  /** Find the nearest map-corner tile to flee toward when BattlePlanner orders a retreat. */
  private retreatPosition(actor: Unit): GridPosition {
    const w = this.map.width;
    const h = this.map.height;
    const corners: GridPosition[] = [
      { col: 2,     row: 2     },
      { col: w - 3, row: 2     },
      { col: 2,     row: h - 3 },
      { col: w - 3, row: h - 3 },
    ];
    return corners.sort((a, b) => {
      const da = Math.hypot(actor.gridPos.col - a.col, actor.gridPos.row - a.row);
      const db = Math.hypot(actor.gridPos.col - b.col, actor.gridPos.row - b.row);
      return da - db;
    })[0];
  }

  private activeBaddieBeacon(): Unit | null {
    return this.activeUnits.find(unit => unit.specialRole === 'baddieBeacon' && unit.alive) ?? null;
  }

  private chaseRadius(actor: Unit, walkToBeacon: boolean): number {
    if (walkToBeacon) return actor.sightRadius;
    if (actor.combatOrder === 'attackTarget') return Infinity;
    if (actor.combatOrder === 'attackMove') return actor.sightRadius * 1.5;
    return actor.sightRadius;
  }

  private tickRevives(players: Unit[], delta: number): void {
    const downed = players.filter((unit) => unit.isDowned && this.isReviveableBandMember(unit));
    const revivers = players.filter((unit) => this.canRevive(unit));

    for (const unit of downed) {
      const helper = revivers.find((candidate) => candidate !== unit && this.dist(candidate, unit) <= BAND_REVIVE_RANGE);
      if (!helper) {
        unit.reviveProgress = Math.max(0, unit.reviveProgress - delta / BAND_REVIVE_TIME);
        continue;
      }

      unit.reviveProgress = Math.min(1, unit.reviveProgress + delta / BAND_REVIVE_TIME);
      if (unit.reviveProgress >= 1) {
        unit.isDowned = false;
        unit.reviveProgress = 0;
        unit.hp = Math.max(1, Math.ceil(unit.maxHp * BAND_REVIVE_HP_RATIO));
      }
    }
  }

  private canAct(unit: Unit): boolean {
    if (!unit.alive || unit.isDowned || unit.stunTimer > 0) return false;
    if (unit.specialRole === 'baddieBeacon') return false;
    if (unit.specialRole === 'corporateHQ') return false;
    if (unit.specialRole === 'streamingMonolith' && unit.isConstructing) return false;
    return true;
  }

  private canBeTargeted(unit: Unit): boolean {
    return unit.alive && !unit.isDowned;
  }

  private canRevive(unit: Unit): boolean {
    return unit.alive && !unit.isDowned && unit.faction === Faction.Player;
  }

  private isReviveableBandMember(unit: Unit): boolean {
    return unit.faction === Faction.Player && unit.bandClass !== null && unit.bandClass !== 'fan';
  }

  private downBandMember(unit: Unit): void {
    unit.hp = 0;
    unit.isDowned = true;
    unit.reviveProgress = 0;
    unit.path = [];
    unit.moveT = 0;
    unit.target = null;
    unit.attackCooldown = 0;
    unit.isMining = false;
    unit.combatOrder = 'idle';
    unit.orderTarget = null;
    unit.orderTargetUnit = null;
  }
}
